import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import { RequestNonprofitPrompt } from '../../RequestNonprofit/RequestNonprofitPrompt/RequestNonprofitPrompt'

import { useAuth, useQuery, useQueryCountryCodeValidation } from '@percent/workplace-giving/common/hooks'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { Page } from '@percent/workplace-giving/common/components/Layout/Page'
import { SearchPageHeader } from '@percent/workplace-giving/common/components/SearchPageHeader/SearchPageHeader'
import { OrganisationsResults } from './Results/OrganisationsResults/OrganisationsResults'
import { usePermission } from '@percent/workplace-giving/common/hooks/usePermission/usePermission'
import { scope } from '@percent/workplace-giving/common/hoc/withPermissions'
import { FundraisersResults } from './Results/FundraisersResults/FundraisersResults'
import { VolunteeringResults } from './Results/VolunteeringResults/VolunteeringResults'
import classes from './GlobalSearch.module.scss'
import { searchFundraisers } from '@percent/workplace-giving/api/search/searchFundraisers/searchFundraisers'
import { searchVerifiedOrganisations } from '@percent/workplace-giving/api/search/searchOrganisations/searchVerifiedOrganisations'
import { OrgSearchFor } from '@percent/workplace-giving/api/search/searchOrganisations/searchVerifiedOrganisations.types'
import { searchOpportunities } from '@percent/workplace-giving/api/search/searchOpportunities/searchOpportunities'
import { ListResponse } from '@percent/workplace-giving/api/goodstack'
import { SearchOpportunitiesResponseRow } from '@percent/workplace-giving/api/search/searchOpportunities/searchOpportunities.types'
import { Flex, IllustratedMessage } from '@percent/lemonade'
import { getPartnerFromAuthState } from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { SwitchToGlobalResults } from '@percent/workplace-giving/common/components/SwitchToGlobalResults/SwitchToGlobalResults'
import { GivingActionsResults } from './Results/GivingActionsResults/GivingActionsResults'
import { getAccountGivingActions } from '@percent/workplace-giving/api/account/givingActions/getAccountGivingActions/getAccountGivingActions'
import { usePostHogFeatureFlag } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { POSTHOG_FEATURE_FLAGS } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'

export const GLOBAL_SEARCH_PAGE_SIZE = 15

export function GlobalSearchPage() {
  const { t } = useTranslation()
  const { track } = useAnalytics()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const has = usePermission()
  const volunteeringEnabled = has(scope('volunteering'))
  const donationsEnabled = has(scope('donation_link'))
  const givingActionsEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.WPG_GIVING_ACTIONS)
  const { state } = useAuth()
  const partner = getPartnerFromAuthState(state)!

  const query = searchParams.get('query') ?? undefined
  const code = searchParams.get('country') ?? undefined

  const countryCode = useQueryCountryCodeValidation({ code })
  const prevCountryCodeRef = useRef<string>('')

  useEffect(() => {
    if (query === undefined) {
      navigate(AppRoute.HOME, { replace: true })
    }
  }, [query, navigate])

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [searchParams])

  const onCountryChange = (country: string) => {
    setSearchParams(prev => {
      prev.set('country', country)

      return prev
    })
  }
  const onQueryChange = (q: string) => {
    setSearchParams(prev => {
      prev.set('query', q)

      return prev
    })
  }

  useEffect(() => {
    if (query?.trim().length) {
      track({
        event: 'Global Search',
        properties: {
          origin: 'Global Search Page',
          countryCode
        }
      })
    }
  }, [query, countryCode, track])

  const { data: organisationsData, isFetching: isFetchingOrganisations } = useQuery(
    [
      'searchVerifiedOrganisations',
      {
        query,
        pageSize: GLOBAL_SEARCH_PAGE_SIZE,
        countryCode,
        for: OrgSearchFor.DonationOrVolunteering
      }
    ],
    searchVerifiedOrganisations,
    {
      enabled: !!query,
      staleTime: 0
    }
  )

  const { data: fundraisersData, isFetching: isFetchingFundraisers } = useQuery(
    ['searchFundraisers', { query, pageSize: GLOBAL_SEARCH_PAGE_SIZE, targetCountries: countryCode }],
    searchFundraisers,
    {
      enabled: !!query && donationsEnabled,
      staleTime: 0
    }
  )

  const { data: volunteeringData, isFetching: isFetchingVolunteering } = useQuery(
    [
      'searchOpportunities',
      {
        query,
        targetCountries: countryCode,
        pageSize: GLOBAL_SEARCH_PAGE_SIZE
      }
    ],
    searchOpportunities,
    {
      enabled: !!query && volunteeringEnabled,
      staleTime: 0
    }
  )

  const { data: givingActionsData, isFetching: isFetchingGivingActions } = useQuery(
    [
      'getAccountGivingActions',
      {
        date: dayjs().format('YYYY-MM-DD'),
        query,
        pageSize: GLOBAL_SEARCH_PAGE_SIZE
      }
    ],
    getAccountGivingActions,
    {
      enabled: !!query && givingActionsEnabled,
      staleTime: 0
    }
  )

  const isLoading =
    isFetchingOrganisations || isFetchingFundraisers || isFetchingVolunteering || isFetchingGivingActions

  const noGlobalResults =
    !organisationsData?.totalResults &&
    !fundraisersData?.totalResults &&
    !volunteeringData?.totalResults &&
    !givingActionsData?.totalResults

  const totalResults = useMemo(() => {
    if (isLoading) {
      return 0
    }

    const filteredTotals = [
      organisationsData?.totalResults,
      fundraisersData?.totalResults,
      volunteeringData?.totalResults
    ].filter(total => total !== undefined)

    let sumTotals = 0
    filteredTotals.forEach(total => {
      if (total) {
        sumTotals += total
      }

      return sumTotals
    })

    return sumTotals
  }, [fundraisersData?.totalResults, isLoading, organisationsData?.totalResults, volunteeringData?.totalResults])

  const switchToGlobal = () => {
    if (countryCode) {
      prevCountryCodeRef.current = countryCode
    }
    setSearchParams(prev => {
      prev.set('country', '')

      return prev
    })
  }

  const switchToCountry = () => {
    const prevCountryCode = prevCountryCodeRef.current

    if (!prevCountryCode) return

    setSearchParams(prev => {
      prev.set('country', prevCountryCode)

      return prev
    })
    prevCountryCodeRef.current = ''
  }

  return (
    <Page testId="global-search-page" fullWidthContent>
      <div>
        <SearchPageHeader
          query={query}
          countryCode={countryCode}
          onCountryChange={onCountryChange}
          onQueryChange={onQueryChange}
        />

        <Flex justify="flex-end" align="center" wrap="wrap" gap="xs" mb="md">
          <SwitchToGlobalResults
            totalResults={totalResults}
            currentCountryCode={countryCode}
            prevCountryCode={prevCountryCodeRef.current}
            switchToGlobal={switchToGlobal}
            switchToCountry={switchToCountry}
            isLoading={isLoading}
          />
        </Flex>

        <div className={classes.ResultsSectionsWrapper}>
          {noGlobalResults && !isLoading ? (
            <>
              <IllustratedMessage
                illustration="no-results"
                title={t('workplace_giving.search.noResultsGlobal', {
                  searchTerm: query,
                  interpolation: { escapeValue: false }
                })}
                description={t(
                  donationsEnabled
                    ? 'workplace_giving.search.noResultDescription'
                    : 'workplace_giving.search.noResultDescriptionNoMatching',
                  { partnerName: partner.name }
                )}
              />
              <RequestNonprofitPrompt eventOrigin="Global Search Page" />
            </>
          ) : (
            <>
              <OrganisationsResults
                data={organisationsData}
                isFetching={isFetchingOrganisations}
                query={query}
                countryCode={countryCode}
              />

              {donationsEnabled ? (
                <FundraisersResults
                  data={fundraisersData}
                  isFetching={isFetchingFundraisers}
                  query={query}
                  countryCode={countryCode}
                />
              ) : null}

              {volunteeringEnabled ? (
                <VolunteeringResults
                  data={volunteeringData as ListResponse<SearchOpportunitiesResponseRow>}
                  isFetching={isFetchingVolunteering}
                  query={query}
                  countryCode={countryCode}
                />
              ) : null}

              {givingActionsEnabled ? (
                <GivingActionsResults data={givingActionsData} isFetching={isFetchingGivingActions} query={query} />
              ) : null}
            </>
          )}
        </div>
      </div>
    </Page>
  )
}
